<template>
  <div v-if="loaded">

      <!-- Row top buttons -->
      <div class="row q-mb-lg">
        <div class="col-6" style="justify-content flex-start">
          <q-btn
            class="capitalize ctrl-button"
            color="grey-5"
            :label="getString('action.toOverview')"
            @click="$router.push(`/navigation`)"
            icon="keyboard_backspace"
          />
        </div>
        <div class="col-6">
          <q-btn
            @click="saveChanges(false)"
            style="float: right"
            class="capitalize ctrl-button right"
            :color="'ctrl-turquoise'"
            :label="`${$store.state.stringsStore.strings.action.save}`"
            icon-right="save"
          />
          <q-btn
            @click="deleteMenu"
            v-if="data && data.can_delete"
            style="float: right"
            class="capitalize ctrl-button right q-mr-md"
            :color="'negative'"
            :label="`${$store.state.stringsStore.strings.action.delete}`"
            icon-right="delete"
          />
        </div>
      </div>

      <div class="row gutter-sm q-mb-md">
        <!-- Left column -->
        <div class="col-md-4 col-sm-12 col-xs-12">
          <div class="card-block">
            <!-- title -->
            <q-field class="q-mb-sm q-mr-sm">
              <q-input type="text" v-model="data.title"
                :float-label="getString('title')">
              </q-input>
            </q-field>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 col-xs-12">
          <div class="card-block">
            <!-- code -->
            <q-field class="q-mb-sm">
              <q-input type="text" v-model="data.code"
                :readonly="!!$route.params.id"
                float-label="Code">
              </q-input>
            </q-field>
          </div>
        </div>
      </div>

      <div class="row gutter-sm">
        <!-- Left column -->
        <div class="col-md-4 col-sm-12 col-xs-12">
          <p class="card-title">Menu items</p>
          <div class="card-block full-block">
            <transition name="fade">
              <div class="new-node" v-if="addNew">{{ newItem.label }}</div>
            </transition>
            <tree :value="menuItems" @drop="handleDrop">
              <div slot-scope="{node, index, path, tree}" @click="getSelected(node)" :class="{ 'node-selected' : node.id === selectedId }">
                <i aria-hidden="true" style="font-size: small;" :class="typeIcon(node.type)"></i>
                <i v-if="node.children.length" @click.stop="tree.toggleFold(node, path)" aria-hidden="true" class="q-icon material-icons menu-item" :class="{ 'menu-item__open' : !node.$folded }" style="font-size: large;">chevron_right</i>
                {{node.label}}
              </div>
            </tree>
        </div>
      </div>
      <div class="col">
        <p class="card-title" v-if="addNew">{{ $store.state.stringsStore.language === 'nl' ? 'Nieuw' : 'New'}}</p>
        <div class="card-block full-block" v-if="addNew">
          <q-field class="q-mb-md">
            <q-select :options="types" v-model="newItem.type"
              :float-label="getString('type')" />
          </q-field>

          <!-- name -->
          <q-field class="q-mb-md">
            <q-input type="text" v-model="newItem.label"
              :float-label="getString('name')">
            </q-input>
          </q-field>

          <!-- LOCATION -->
          <!-- external: url -->
          <q-field class="q-mb-md" v-if="newItem.type === 'external'">
            <q-input type="text" v-model="newItem.location"
              :float-label="getString('url')">
            </q-input>
          </q-field>

          <!-- Target -->
          <q-field class="q-mb-md">
            <q-select :options="anchorTargetOptions" v-model="newItem.target"
              float-label="Target" />
          </q-field>

          <!-- module: autocomplete -->
          <q-field class="q-mb-md" v-if="['module', 'route'].includes(newItem.type)">
            <q-search clearable v-model="newItem.route.label"
              :float-label="getString('module.navigation.fields.module')">
              <q-autocomplete :max-results="25" value-field="label" :min-characters="2" @search="searchModule(newItem, ...arguments)" @selected="selectModule(newItem, ...arguments)" />
            </q-search>
          </q-field>

          <!-- page: autocomplete -->
          <q-field class="q-mb-md" v-if="newItem.type === 'page'">
            <q-search clearable v-model="newItem.page.name" :float-label="getString('module.navigation.fields.page')">
              <q-autocomplete :max-results="25" value-field="name" :min-characters="2" @search="searchPage(newItem, ...arguments)" @selected="selectPage(newItem, ...arguments)"/>
            </q-search>
          </q-field>

          <!-- SUBMENU -->
          <q-field class="q-mb-md" v-if="newItem.type === 'page' || (newItem.type === 'module' && newItem.subType === 'route')">
            <q-search clearable v-model="newItem.menu.title" :float-label="'Submenu'">
              <q-autocomplete :max-results="25" value-field="title" :min-characters="2" @search="searchMenu(newItem, ...arguments)" @selected="selectMenu(newItem, ...arguments)"/>
            </q-search>
          </q-field>

          <!-- module: subitems -->
          <q-field class="q-mb-md" v-if="newItem.type === 'module' && newItem.subType === 'module'">
              <q-toggle class="q-mt-md" color="secondary"
                v-model="newItem.subitems">
                <span style="margin-left: 1em;">
                 Subitems
                </span>
              </q-toggle>
          </q-field>

          <div class="card-bottom">
            <q-btn
              class="capitalize ctrl-button q-mr-md"
              color="primary"
              :label="getString('action.save')"
              @click="saveNew"
              icon="save"
            />

            <q-btn
              class="capitalize ctrl-button"
              color="grey-5"
              :label="getString('action.cancel')"
              @click="cancelItem"
              icon="close"
            />
          </div>
        </div>

        <div class="row q-mb-sm" v-else-if="editItem && selected">

          <div class="full-width">
          <p class="card-title">Details</p>
            <div class="card-block full-block">

              <!-- name -->
              <q-field class="q-mb-md">
                <q-input type="text" v-model="selected.label"
                  :float-label="getString('name')">
                </q-input>
              </q-field>

              <!-- module: autocomplete -->
              <q-field class="q-mb-md" v-if="selected.type === 'module' && selected.route">
                <q-search clearable v-model="selected.route.label"
                  :float-label="getString('module.navigation.fields.module')">
                  <q-autocomplete :max-results="100" value-field="label" :min-characters="2" @search="searchModule(selected, ...arguments)" @selected="selectModule(selected, ...arguments)" />
                </q-search>
              </q-field>

              <!-- external: url -->
              <q-field class="q-mb-md" v-if="selected.type === 'external'">
                <q-input type="text" v-model="selected.location"
                  :float-label="getString('name')">
                </q-input>
              </q-field>

              <q-field class="q-mb-md" v-if="selected.type !== 'page'">
                <q-input type="text" v-model="selected.location"
                  :float-label="getString(null, 'Location', 'Locatie')">
                </q-input>
              </q-field>

              <!-- page: autocomplete -->
              <q-field class="q-mb-md" v-if="selected.type === 'page'">
                <q-search clearable v-model="selected.page.name" :float-label="getString('module.navigation.fields.page')">
                  <q-autocomplete :max-results="100" value-field="name" :min-characters="2" @search="searchPage(selected, ...arguments)" @selected="selectPage(selected, ...arguments)"/>
                </q-search>
              </q-field>

              <!-- Target -->
              <q-field class="q-mb-md">
                <q-select :options="anchorTargetOptions" v-model="selected.target"
                  float-label="Target" />
              </q-field>

              <!-- SUBMENU -->
              <q-field class="q-mb-md" v-if="selected.type === 'page' || (selected.type === 'module' && selected.subType === 'route')">
                <q-search clearable v-model="selected.menu.title" :float-label="'Submenu'">
                  <q-autocomplete :max-results="100" value-field="title" :min-characters="2" @search="searchMenu(selected, ...arguments)" @selected="selectMenu(selected, ...arguments)"/>
                </q-search>
              </q-field>

              <!-- module: subitems -->
              <q-field class="q-mb-md" v-if="selected.type === 'module' && selected.subType === 'module'">
                  <q-toggle class="q-mt-md" color="secondary"
                    v-model="selected.subitems">
                    <span style="margin-left: 1em;">
                      Subitems
                    </span>
                  </q-toggle>
              </q-field>

              <!-- If the menu has icons -->
              <div v-if="data.has_icons" class="q-mt-lg" style="display: flex;">
                <!-- Icon -->
                <Icon :icon="selected.icon" style="font-size: 60px;" class="q-mr-md" />
                <!-- Search for icons -->
                <div style="flex-grow: 1;">
                  <q-field>
                    <q-search clearable v-model="selected.icon" :float-label="'Icon'">
                      <q-autocomplete :max-results="10" value-field="icon" :min-characters="2" @search="searchIcons(selected.icon, ...arguments)"
                      @selected="selectIcon(selected, ...arguments)" />
                    </q-search>
                  </q-field>
                  <div style="color: #999; font-size: 12px;" class="q-mt-sm">
                    {{  getString(null, 'Available icons can be found here:', 'Beschikbare iconen zijn hier te vinden:') }}
                    <a style="color: #6cc6e3; text-decoration: none;  font-weight: bold;" href="https://icon-sets.iconify.design/mdi/" target="_blank">https://icon-sets.iconify.design/mdi/</a>
                  </div>
                </div>
              </div>

              <div class="card-bottom">
                <q-btn
                  class="capitalize ctrl-button q-mr-md"
                  color="primary"
                  :label="getString('action.save')"
                  @click="saveExisting"
                  icon="save"
                />

                <q-btn
                  class="capitalize ctrl-button"
                  color="grey-5"
                  :label="getString('action.cancel')"
                  @click="cancelEdit"
                  icon="close"
                />

                <q-btn
                  class="capitalize ctrl-button q-ml-md"
                  color="negative"
                  :label="getString('action.delete')"
                  @click="deleteItem"
                  icon="delete"
                />
              </div>

            </div>
          </div>
        </div>

        <div class="row q-mb-sm" v-else>

          <div class="full-width">
          <p class="card-title">Details</p>
            <div class="card-block full-block">
              <q-btn
                class="capitalize ctrl-button"
                color="primary"
                :label="getString('module.navigation.item.new')"
                @click="add"
                icon="add"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getString from '@/helpers/GetStrings';
import { Tree, Draggable, Fold } from 'he-tree-vue';
import 'he-tree-vue/dist/he-tree-vue.css';
import qs from 'qs';
import errorHandler from '@/mixins/detailViews/errorHandler';
import saveChanges from '@/mixins/detailViews/saveChanges';
import { Icon } from '@iconify/vue2';

export default {

  components: {
    Icon,
    Tree: Tree.mixPlugins([Draggable, Fold]),
  },

  mixins: [saveChanges, errorHandler],

  data() {
    return {
      endpoint: 'navigation',
      module: 'navigation.menu',
      data: {
        code: '',
        title: '',
        has_icons: false,
      },
      selectedId: null,
      addNew: false,
      editItem: false,
      placeholder: '',
      searchTermPage: '',
      searchTermModule: '',
      newType: null,
      newItem: {},
      structure: '',
      selected: {},
      nodes: [],
      types: [
        {
          label: 'Page',
          value: 'page',
        },
        {
          label: 'External',
          value: 'external',
        },
        {
          label: 'Module',
          value: 'module',
        },
      ],
      // List of anchor target options.
      anchorTargetOptions: [
        { label: getString(null, 'Current frame', 'Zelfde tabblad'), value: '_self' },
        { label: getString(null, 'New tab', 'Nieuwe tabblad'), value: '_blank' },
      ],
      loaded: false,

      menuItems: [],
      items: [],
    };
  },
  methods: {
    getString,

    typeIcon(type) {
      const className = 'q-icon mdi q-item-icon';

      let icon;

      switch (type) {
        case 'route':
          icon = 'mdi-application';
          break;
        case 'module':
          icon = 'mdi-application';
          break;
        case 'external':
          icon = 'mdi-open-in-new';
          break;
        case 'page':
          icon = 'mdi-file-outline';
          break;
        default:
          break;
      }
      return `${className} ${icon}`;
    },

    requestParams() {
      return {
        code: this.data.code,
        title: this.data.title,
      };
    },

    getMenu() {
      this.axios.get(`navigation/${this.$route.params.id}`).then((res) => {
        this.data = res.data.menu;
      }).then(() => {
        this.loaded = true;
      });
    },

    searchIcons(_, searchTerm, done) {
      fetch(`https://api.iconify.design/search?query=${searchTerm}`)
        .then(response => response.json())
        .then((data) => {
          done(data.icons.filter(icon => icon.includes('mdi')).map(icon => ({
            label: icon,
            value: icon,
          })));
        });
    },

    deleteMenu() {
      this.axios.delete(`navigation/${this.$route.params.id}`).then(() => {
        this.$q.notify({
          message: getString('module.navigation.menu.edit.delete.success'),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
        this.$router.push('/navigation');
      }).catch((err) => {
        // Handle the error (handleError mixin)
        this.handleError(err);
      });
    },

    deleteItem() {
      this.axios.delete(`navigation/${this.$route.params.id}/items/${this.selected.id}`).then(() => {
        this.$q.notify({
          message: getString('module.navigation.item.edit.delete.success'),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
        this.editItem = false;
        this.refreshTree();
      }).catch((err) => {
        // Handle the error (handleError mixin)
        this.handleError(err);
      });
    },

    getSelected(node) {
      this.selectedId = node.id;

      this.cancelItem();
      this.editItem = true;
      this.axios.get(`navigation/${this.$route.params.id}/items/${node.id}`).then((response) => {
        this.selected = response.data.item;
        this.selected.menu = this.selected.menu || {
          title: '',
        };

        if (this.selected.type === 'route') {
          this.selected.type = 'module';
          this.selected.subType = 'route';
        }

        if (this.selected.type === 'page') {
          this.selected.page = this.selected.page || { name: null, id: null };
        }
      });
    },

    async handleDrop(node) {
      // A node is only nested when the target path is an array with length > 1.
      if (node.targetPath.length > 1) {
        let target = this.menuItems;

        for (let index = 0; index < node.targetPath.length - 1; index += 1) {
          if (target.children) {
            target = target.children[node.targetPath[index]];
          } else {
            target = target[node.targetPath[index]];
          }
        }

        // The sortId is the last index in the targetPath array plus 1 (sort_id begins at 1).
        const sortId = node.targetPath.pop() + 1;

        // Set the parent_id of the node to the id of the target.
        await this.sortNode(node.dragNode.id, target.id, sortId);

        this.$nextTick(() => this.refreshTree());
      } else {
        await this.sortNode(node.dragNode.id, 0, node.targetPath[0]);
      }
    },

    sortNode(nodeId, parentId, sortId) {
      return this.axios.put(`/navigation/${this.$route.params.id}/items/${nodeId}/location`, qs.stringify({ parent_id: parentId, sort_id: sortId }));
    },

    refreshTree() {
      this.axios.get(`navigation/${this.$route.params.id}/items`).then(async (res) => {
        this.items = res.data.items;
        // Return a promise for processing the menu.
        await Promise.all(this.items.map(item => this.formatItem(item))).then((data) => {
          this.menuItems = data;
        });
      }).finally(() => {
        this.loaded = true;
      });
    },

    /**
     * Get the icon for the menu item based on the type.
     */
    getIcon(type) {
      let icon;

      switch (type) {
        case 'page':
          icon = 'mdi mdi-menu-right q-item-icon';
          break;
        case 'module':
          icon = 'mdi mdi-page-layout-sidebar-left q-item-icon';
          break;

        default:
          break;
      }

      return icon;
    },

    searchMenu(menuItem, terms, done) {
      // Reset page id on input
      if (menuItem.menu.id) {
        menuItem.menu.id = null;
      }
      menuItem.menu_id = null;
      this.axios.get(`navigation?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.menus.map(item => ({ label: item.title, id: item.id }));
        done(suggestions);
      });
    },

    selectMenu(item, found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.menu.title = found.label;
      item.menu_id = found.id;
    },

    selectIcon(item, found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.icon = found.value;
    },

    /**
     * Search for pages using the autocomplete
     */
    searchPage(menuItem, terms, done) {
      // Reset page id on input
      if (menuItem.page) {
        menuItem.page.id = null;
      }
      menuItem.page_id = null;

      this.axios.get(`page?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.pages.map(item => ({ label: item.name, id: item.id }));
        done(suggestions);
      });
    },

    selectPage(item, found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.page.name = found.label;
      item.page_id = found.id;
    },

    /**
     * Search for routes and modules using the autocomplete
     */
    searchModule(menuItem, terms, done) {
      // Reset location on input
      menuItem.location = null;

      this.axios.get(`navigation/routes?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.routes.map(item => ({ label: item.label, id: item.id, value: item.value, type: item.type }));
        done(suggestions);
      });
    },

    selectModule(item, found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      item.type = 'module';

      item.subType = found.type;
      item.location = found.value;

      this.searchTermModule = found.label;
    },

    /**
     * Format menu item
     */
    async formatItem(item) {
      if (item.children) {
        item.children = await Promise.all(item.children.map(child => this.formatItem(child)));
      }

      return new Promise((resolve) => {
        resolve({
          id: item.id,
          label: item.label,
          value: item.label,
          $droppable: item.type !== 'module',
          parent_id: 0,
          children: item.children || [],
          type: item.type,
          icon: this.getIcon(item.type),
        });
      });
    },

    /**
     * Save an existing menu item, the paramaters sent depend of the type of item.
     */
    async saveExisting() {
      const params = this.getParams(this.selected);

      await this.axios.put(`/navigation/${this.$route.params.id}/items/${this.selected.id}`, qs.stringify(params)).then(() => {
        this.$q.notify({
          message: getString('module.navigation.item.edit.save.success'),
          type: 'positive',
          timeout: 1200,
          position: 'top-right',
        });
        this.refreshTree();
      }).catch((err) => {
        // Handle the error (handleError mixin)
        this.handleError(err);
      });
    },

    async saveNew() {
      const params = this.getParams(this.newItem);
      await this.axios.post(`/navigation/${this.$route.params.id}/items/`, qs.stringify(params));
      return true;
    },

    getParams(item) {
      let params = {
            parent_id: item.parent_id || 0,
            label: item.label,
            icon: item.icon,
            target: item.target,
      };

      switch (item.type) {
        case 'page':
          params.type = item.type;
          params.page_id = item.page_id || (item.page && item.page.id);
          params.menu_id = item.menu_id || null;
          break;
        case 'external':
          params.type = item.type;
          params.location = item.location;
          break;
        case 'module':
        case 'route':
          params.menu_id = item.menu_id || null;
          params.type = item.subType;
          params.location = item.location;
          params.subitems = item.subitems ? '1' : '0';
          break;
        default:
          break;
      }
      Object.keys(params).forEach(key => (params[key] == null) && delete params[key]);

      return params;
    },

    cancelEdit() {
      this.editItem = false;
    },

    /**
     * Callback to reject the new node.
     */
    cancelItem() {
      this.addNew = false;
      // this.newItem = {};
      this.menuItems = [];
      this.refreshTree();
      return true;
    },

    add() {
      if (this.addNew) {
        return;
      }
      this.addNew = true;
      this.newItem = {
        label: 'New',
        menu: {
          title: null,
        },
        route: {
          label: null,
        },
        page: {
          name: '',
        },
        page_id: null,
        subitems: false,
        menu_id: null,
        parent_id: 0,
        location: null,
        type: null,
        new: true,
        children: [],
      };

      this.addNewItem().then(() => {
        // Post the new item to the server.
        this.axios.post(`navigation/${this.$route.params.id}/items`, qs.stringify(this.getParams(this.newItem))).then(() => {
          this.$q.notify({
            message: getString('module.navigation.edit.save.success'),
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
          this.addNew = false;
          this.refreshTree();
        }).catch((err) => {
          // Handle the error (handleError mixin)
          this.handleError(err);
        });
      });
    },

    /**
     * An async function which is resolved when an item is added and rejected when the addition is canceled.
     */
    addNewItem() {
      this.cancelEdit();
      return new Promise((resolve) => {
        this.saveNew = resolve;
      });
    },

  },
  mounted() {
    if (this.$route.params.id) {
      this.getMenu();
    }
    this.refreshTree();
  },
};
</script>

<style lang="stylus">
  .new-node
    background #71CFED17
    border: 1px solid var(--q-color-primary);
    margin-bottom: 5px;
    padding: 5px;

  .tree-node
    cursor pointer
    border none !important
    span
      display block
      width 100%

.tree-node-back:hover
  background rgba(0, 0, 0, 0.1)

 .menu
   &__new-item
    text-align center
    margin-top 10px
    padding 10px
    color #fff
    background var(--q-color-primary)
    border 1px solid #71CFED17
    border-radius 5px
    cursor pointer

.submenu
  &__item
    cursor pointer
    padding 10px
    font-weight bold
    background #71CFED17
    border 1px solid var(--q-color-primary)
    border-radius 3px
    margin-bottom 2px

.card-bottom
  position absolute
  left 20px
  bottom 20px

.full-block
    position relative
    height calc(100vh - 320px)
    max-height calc(100vh - 195px)
    overflow auto

.menu-item
  transition all 0.2s
  &__open
    transform rotate(90deg)

.node-selected
  font-weight bold
</style>
